import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useState } from "react";

interface NavigationContextValue {
    /** Whether the navigation is collapsed (icons only) */
    isCollapsed: boolean;
    /** Toggle the collapsed state */
    toggleCollapsed: () => void;
    /** Set collapsed state directly */
    setCollapsed: (collapsed: boolean) => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

interface NavigationProviderProps {
    children: ReactNode;
    /** Initial collapsed state */
    defaultCollapsed?: boolean;
}

/**
 * Provider for navigation collapse state
 * Persists state in localStorage for user preference
 */
export const NavigationProvider = ({ children, defaultCollapsed = false }: NavigationProviderProps) => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const stored = localStorage.getItem("navigation-collapsed");
        return stored ? JSON.parse(stored) : defaultCollapsed;
    });

    const setCollapsed = useCallback((collapsed: boolean) => {
        setIsCollapsed(collapsed);
        localStorage.setItem("navigation-collapsed", JSON.stringify(collapsed));
    }, []);

    const toggleCollapsed = useCallback(() => {
        setCollapsed(!isCollapsed);
    }, [isCollapsed, setCollapsed]);

    return (
        <NavigationContext.Provider value={{ isCollapsed, toggleCollapsed, setCollapsed }}>
            {children}
        </NavigationContext.Provider>
    );
};

/**
 * Hook to access navigation collapse state
 */
export const useNavigationCollapse = (): NavigationContextValue => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error("useNavigationCollapse must be used within a NavigationProvider");
    }
    return context;
};

export default NavigationContext;
